import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import { Button } from '~/shared/shadcn/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/shared/shadcn/ui/popover';
import { Calendar } from '~shared/shadcn/calendar';
import { useIsMobile } from '~shared/shadcn/hooks/use-mobile';
import { cn } from '~shared/shadcn/lib/utils';

interface DateRangePickerProps {
  className?: string;
  date: DateRange | undefined;
  setDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
}

export function DateRangePicker({
  className,
  date,
  setDate,
}: DateRangePickerProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  // Draft range so mid-selection clicks do not refetch / remount the table
  // (ENR-3423 Class Charge History hang).
  const [draft, setDraft] = useState<DateRange | undefined>(date);

  useEffect(() => {
    if (open) {
      setDraft(date);
    }
  }, [open, date]);

  const commitRange = (next: DateRange | undefined) => {
    setDraft(next);
    if (next?.from && next?.to) {
      setDate(next);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      // Closing with only a start date still applies it so the trigger stays in sync.
      if (draft?.from) {
        setDate({
          from: draft.from,
          to: draft.to ?? draft.from,
        });
      }
    }
    setOpen(nextOpen);
  };

  const display = open ? draft : date;

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            id='date'
            variant={'outline'}
            className={cn(
              'w-[300px] justify-start text-left font-normal',
              !display && 'text-muted-foreground',
            )}
          >
            <CalendarIcon className='mr-2 size-4' />
            {display?.from ? (
              display.to ? (
                <>
                  {format(display.from, 'LLL dd, y')} -{' '}
                  {format(display.to, 'LLL dd, y')}
                </>
              ) : (
                format(display.from, 'LLL dd, y')
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <Calendar
            mode='range'
            defaultMonth={display?.from}
            selected={draft}
            onSelect={commitRange}
            numberOfMonths={isMobile ? 1 : 2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
